<script setup>
import { computed, ref, watch } from 'vue';
import XlModal from '../xl/XlModal.vue';
import XlButton from '../xl/XlButton.vue';
import BrokerPicker from './BrokerPicker.vue';
import { bulkAssignBroker } from '../../api/policyholders';
import { plural } from '../../helpers/plural';

const props = defineProps({
  // Each entry needs at least { id, broker_id } — names are only used for the
  // singular copy and the failure list.
  selection: { type: Array, default: () => [] },
  brokers: { type: Array, default: () => [] },
});

const emit = defineEmits(['close', 'assigned']);

const target = ref(null);
const moveAll = ref(true);
const loading = ref(false);
const failures = ref([]);

watch(() => props.selection, () => {
  target.value = null;
  moveAll.value = true;
  failures.value = [];
});

const nameOf = person =>
  person.fullname || `${person.first_name ?? ''} ${person.last_name ?? ''}`.trim() || person.email || 'This person';

const total = computed(() => props.selection.length);
const held = computed(() => props.selection.filter(p => p.broker_id));
const free = computed(() => props.selection.filter(p => !p.broker_id));
const isMixed = computed(() =>
  free.value.length > 0 && held.value.some(p => p.broker_id !== target.value)
);

const targetBroker = computed(() => props.brokers.find(b => b.id === target.value) || null);
const brokerName = computed(() =>
  targetBroker.value
    ? `${targetBroker.value.first_name ?? ''} ${targetBroker.value.last_name ?? ''}`.trim() || targetBroker.value.email
    : ''
);
const brokerFirstName = computed(() => brokerName.value.split(' ')[0] || '');

const alreadyTargets = computed(() =>
  target.value === null ? [] : props.selection.filter(p => p.broker_id === target.value)
);

const scope = computed(() => (isMixed.value && !moveAll.value ? free.value : props.selection));
const changing = computed(() => scope.value.filter(p => p.broker_id !== target.value));
const movingFromSomeone = computed(() => changing.value.filter(p => p.broker_id).length);

const state = computed(() => {
  if (target.value !== null && changing.value.length === 0) return 'nothing';
  if (alreadyTargets.value.length > 0) return 'D';
  if (isMixed.value) return 'C';
  if (held.value.length === total.value && total.value > 0) return 'B';
  return 'A';
});

const title = computed(() =>
  state.value === 'B' || (state.value === 'D' && free.value.length === 0)
    ? 'Move to another broker'
    : 'Assign a broker'
);

const sentence = computed(() => {
  // First name, matching the button labels: "Assign 12 to Maya", "3 are
  // already Maya's". The picker directly below carries the full names.
  const named = brokerFirstName.value;

  if (state.value === 'nothing') return `All of these are already ${named}'s.`;

  if (state.value === 'D') {
    return `${total.value} selected. ${plural(alreadyTargets.value.length, 'is', 'are')} already ${named}'s and won't change.`;
  }

  if (state.value === 'C') {
    return `${total.value} selected. ${plural(held.value.length, 'already has', 'already have')} a broker and will move from them.`;
  }

  if (state.value === 'B') {
    return named
      ? `All ${total.value} of these already have a broker. They'll move to ${named}.`
      : `All ${total.value} of these already have a broker.`;
  }

  if (total.value === 1) return `${nameOf(props.selection[0])} has no broker yet.`;

  return `${total.value} policyholders selected.`;
});

const buttonLabel = computed(() => {
  if (target.value === null) return 'Pick a broker';
  if (state.value === 'nothing') return `Already ${brokerFirstName.value}'s`;

  // A count even for one person: a name would resize the button with every selection.
  const verb = movingFromSomeone.value ? 'Move' : 'Assign';

  return `${verb} ${changing.value.length} to ${brokerFirstName.value}`;
});

const canConfirm = computed(() => target.value !== null && changing.value.length > 0 && !loading.value);

const confirm = async () => {
  if (!canConfirm.value) return;

  loading.value = true;
  failures.value = [];

  try {
    const result = await bulkAssignBroker(
      props.selection.map(p => p.id),
      target.value,
      isMixed.value && !moveAll.value
    );

    const failed = Object.keys(result.failed || {});

    if (failed.length) {
      failures.value = failed.map(id => nameOf(props.selection.find(p => String(p.id) === String(id)) || {}));
      return;
    }

    emit('assigned', { ...result, brokerName: brokerName.value, moved: result.moved || [] });
  } catch (error) {
    failures.value = [error.response?.data?.message || 'The assignment could not be saved.'];
  } finally {
    loading.value = false;
  }
};
</script>

<template>
  <XlModal :title="title" :subtitle="sentence" max-width="560px" @close="emit('close')">
    <div class="bulk-assign" data-testid="bulk-assign">
      <BrokerPicker
        v-model="target"
        :brokers="brokers"
        :will-change="changing.length"
      />

      <div v-if="isMixed" class="bulk-assign__choice" data-testid="bulk-assign-choice">
        <label class="bulk-assign__option">
          <input type="radio" name="bulk-assign-scope" :checked="moveAll" @change="moveAll = true" />
          <span>Move all {{ total - alreadyTargets.length }} to {{ brokerFirstName || 'the broker you pick' }}</span>
        </label>
        <label class="bulk-assign__option">
          <input type="radio" name="bulk-assign-scope" :checked="!moveAll" @change="moveAll = false" />
          <span>Only the {{ free.length }} with nobody</span>
        </label>
      </div>

      <div v-if="failures.length" class="bulk-assign__failures" data-testid="bulk-assign-failures">
        <b>{{ failures.length }} failed.</b>
        <span>{{ failures.join(', ') }}</span>
      </div>
    </div>

    <template #actions>
      <XlButton variant="ghost" size="sm" :disabled="loading" @click="emit('close')">Cancel</XlButton>
      <XlButton size="sm" :disabled="!canConfirm" :loading="loading" data-testid="bulk-assign-confirm" @click="confirm">{{ failures.length ? 'Retry' : buttonLabel }}</XlButton>
    </template>
  </XlModal>
</template>

<style lang="scss" scoped>
.bulk-assign {
  display: flex;
  flex-direction: column;
  gap: 16px;
  font-family: var(--xl-font-family);

  &__choice {
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 14px 16px;
    background: var(--xl-color-primary-container);
    border-radius: var(--xl-radius-md);
  }

  &__option {
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: var(--xl-type-body-medium-size);
    line-height: var(--xl-type-body-medium-line);
    color: var(--xl-color-on-primary-container);
    cursor: pointer;
  }

  &__failures {
    display: flex;
    flex-direction: column;
    gap: 4px;
    padding: 12px 14px;
    border-radius: var(--xl-radius-sm);
    background: color-mix(in srgb, var(--xl-color-error) 14%, white);
    color: var(--xl-color-error);
    font-size: var(--xl-type-body-medium-size);
    line-height: var(--xl-type-body-medium-line);
  }
}
</style>
